import moment from "moment";

/**
 * CSV round-trip for activity dates.
 *
 * The download is both the template and the current state: every activity is
 * exported with the dates it has now, so the file is filled in place rather
 * than from scratch. The upload reads it back into the editor's drafts — it
 * writes nothing on its own. Everything downstream (billing milestones,
 * contractor payouts, the project window) is derived from those drafts by the
 * usual preview, exactly as if the dates had been picked by hand.
 */

export const CSV_COLUMNS = ["activity_id", "activity", "start_date", "end_date"];

// What a date may look like coming back out of Excel. Deliberately no
// MM/DD/YYYY: it cannot be told apart from DD/MM/YYYY for the first twelve
// days of a month, and the editor's pickers are DD/MM, so guessing wrong would
// silently move a date by months.
const DATE_FORMATS = [
  "YYYY-MM-DD",
  "DD/MM/YYYY",
  "DD-MM-YYYY",
  "DD.MM.YYYY",
  "DD MMM YYYY",
  "DD MMM, YYYY",
];

const OUTPUT_FORMAT = "YYYY-MM-DD";

// Header aliases, so a file that has been through Excel and had its headers
// prettified still loads.
const HEADER_ALIASES = {
  activity_id: ["activity_id", "activityid", "id"],
  activity: ["activity", "activity_name", "name"],
  start_date: ["start_date", "startdate", "start"],
  end_date: ["end_date", "enddate", "end"],
};

const normaliseHeader = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const escapeCell = (value) => {
  const text = value == null ? "" : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Every activity, dated or not — a blank cell is the prompt to fill it in. */
export const buildScheduleCsv = (activities = []) =>
  [CSV_COLUMNS.join(",")]
    .concat(
      activities.map((activity) =>
        [
          activity.id,
          activity.name,
          activity.start_date ?? "",
          activity.end_date ?? "",
        ]
          .map(escapeCell)
          .join(",")
      )
    )
    // CRLF: Excel is the tool on the other end of this.
    .join("\r\n");

export const downloadCsv = (filename, contents) => {
  const blob = new Blob([contents], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Character-by-character rather than split(","): activity names contain commas
 * and quotes, and Excel escapes them by doubling the quote.
 */
const parseRows = (text) => {
  // Excel writes a UTF-8 BOM; left in place it becomes part of the first header.
  const source = String(text ?? "").replace(/^\uFEFF/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;

  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];

    if (quoted) {
      if (char !== '"') {
        cell += char;
      } else if (source[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else {
        quoted = false;
      }
      continue;
    }

    if (char === '"') {
      quoted = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && source[i + 1] === "\n") i += 1;
      row.push(cell);
      cell = "";
      rows.push(row);
      row = [];
    } else {
      cell += char;
    }
  }

  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }

  // Trailing newlines and Excel's habit of padding with empty rows.
  return rows.filter((cells) => cells.some((value) => value.trim() !== ""));
};

const indexHeaders = (headerRow) => {
  const seen = headerRow.map(normaliseHeader);
  const index = {};

  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    const position = seen.findIndex((header) => aliases.includes(header));
    if (position !== -1) index[field] = position;
  }

  return index;
};

/** null for an empty cell (a deliberate clear), undefined when unparseable. */
const readDate = (raw) => {
  const text = String(raw ?? "").trim();
  if (!text) return null;

  const parsed = moment(text, DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format(OUTPUT_FORMAT) : undefined;
};

/**
 * Read a filled-in template against the activities on screen.
 *
 * Rows are matched on activity_id, falling back to an exact name match for a
 * file that has lost the id column. Locked activities are reported rather than
 * applied — the same rule the date pickers follow, so the two cannot disagree.
 *
 * @returns {{ drafts: object, applied: number, cleared: number, issues: object }}
 */
export const parseScheduleCsv = (text, activities = []) => {
  const rows = parseRows(text);

  if (!rows.length) {
    return { error: "The file is empty." };
  }

  const headers = indexHeaders(rows[0]);

  if (headers.activity_id === undefined && headers.activity === undefined) {
    return {
      error:
        "No 'activity_id' or 'activity' column found. Download the template and fill that in.",
    };
  }
  if (headers.start_date === undefined && headers.end_date === undefined) {
    return {
      error: "No 'start_date' or 'end_date' column found.",
    };
  }

  const byId = new Map(activities.map((a) => [String(a.id), a]));

  // Only unambiguous names are usable as a fallback key; two activities called
  // "Painting" would otherwise have one silently overwrite the other.
  const nameCounts = new Map();
  for (const activity of activities) {
    const key = String(activity.name ?? "").trim().toLowerCase();
    nameCounts.set(key, (nameCounts.get(key) || 0) + 1);
  }
  const byName = new Map(
    activities
      .filter(
        (a) => nameCounts.get(String(a.name ?? "").trim().toLowerCase()) === 1
      )
      .map((a) => [String(a.name ?? "").trim().toLowerCase(), a])
  );

  const drafts = {};
  const issues = { locked: [], unknown: [], invalidDate: [], endBeforeStart: [] };
  let applied = 0;
  let cleared = 0;

  for (const cells of rows.slice(1)) {
    const at = (position) =>
      position === undefined ? "" : (cells[position] ?? "");

    const rawId = String(at(headers.activity_id)).trim();
    const rawName = String(at(headers.activity)).trim();
    const label = rawName || (rawId ? `#${rawId}` : "(unnamed row)");

    const activity =
      (rawId && byId.get(rawId)) ||
      (rawName && byName.get(rawName.toLowerCase())) ||
      null;

    if (!activity) {
      issues.unknown.push(label);
      continue;
    }

    if (!activity.editable) {
      issues.locked.push(activity.name);
      continue;
    }

    const start =
      headers.start_date === undefined
        ? undefined
        : readDate(at(headers.start_date));
    const end =
      headers.end_date === undefined ? undefined : readDate(at(headers.end_date));

    // undefined means the cell held something that is not a date. A blank cell
    // reads as null, which is a legitimate instruction to clear the date.
    if (start === undefined && headers.start_date !== undefined) {
      issues.invalidDate.push(`${activity.name} (start)`);
    }
    if (end === undefined && headers.end_date !== undefined) {
      issues.invalidDate.push(`${activity.name} (end)`);
    }

    const draft = {};
    if (start !== undefined && start !== (activity.start_date ?? null)) {
      draft.start_date = start;
    }
    if (end !== undefined && end !== (activity.end_date ?? null)) {
      draft.end_date = end;
    }

    if (!Object.keys(draft).length) continue;

    // Flagged, not rejected: the backend blocks this on save anyway, and the
    // row is easier to fix on screen than in the spreadsheet.
    const finalStart =
      draft.start_date !== undefined ? draft.start_date : activity.start_date;
    const finalEnd =
      draft.end_date !== undefined ? draft.end_date : activity.end_date;
    if (finalStart && finalEnd && finalEnd < finalStart) {
      issues.endBeforeStart.push(activity.name);
    }

    if (draft.start_date === null || draft.end_date === null) cleared += 1;

    drafts[activity.id] = draft;
    applied += 1;
  }

  return { drafts, applied, cleared, issues };
};
